<template>
  <div id="app" @keydown.esc="openMenu = null; showThemePicker = false; showChangelog = false">

    <!-- ── Top Navbar ── -->
    <nav class="app-navbar">
      <router-link to="/dashboard" class="nav-brand" active-class="">
        <span class="brand-icon">◈</span>
        <span class="brand-text">HDWallet</span>
      </router-link>

      <div class="nav-links">
        <router-link to="/dashboard" class="nav-lnk" active-class="nav-lnk-on">
          📊 Dashboard
        </router-link>

        <div v-for="c in chains" :key="c.key" class="nav-drop-wrap">
          <button
            class="nav-lnk chain-pill"
            :class="isChainActive(c.key) ? 'nav-lnk-on chain-pill-active' : ''"
            :style="isChainActive(c.key) ? `border-color:${c.color};color:${c.color};background:${c.color}18` : ''"
            @click.stop="openMenu = openMenu === c.key ? null : c.key"
          >
            <img :src="c.logo" class="chain-pill-icon" :alt="c.label" />
            {{ c.label }}
            <span class="drop-caret" :class="openMenu === c.key ? 'caret-up' : ''">▾</span>
          </button>

          <transition name="drop-fade">
            <div v-if="openMenu === c.key" class="nav-drop-menu" @click="openMenu = null">
              <router-link :to="'/' + c.key" class="nav-drop-item">
                <span class="drop-icon">💼</span> Wallet
              </router-link>
              <router-link :to="'/' + c.key + '/transactions'" class="nav-drop-item">
                <span class="drop-icon">📋</span> Transactions
              </router-link>
            </div>
          </transition>
        </div>

        <router-link to="/payout"   class="nav-lnk" active-class="nav-lnk-on">💸 Payout</router-link>
        <router-link to="/history"  class="nav-lnk" active-class="nav-lnk-on">📒 History</router-link>
        <router-link to="/addresses" class="nav-lnk" active-class="nav-lnk-on">📖 Addresses</router-link>
        <router-link to="/accounts" class="nav-lnk" active-class="nav-lnk-on">👛 Accounts</router-link>
        <router-link to="/settings" class="nav-lnk" active-class="nav-lnk-on">⚙️ Settings</router-link>
      </div>

      <div class="nav-right">

        <!-- Theme picker -->
        <div class="theme-wrap" style="position:relative">
          <button
            class="nav-theme-btn"
            :class="showThemePicker ? 'nav-theme-btn-on' : ''"
            @click.stop="showThemePicker = !showThemePicker"
            title="Theme & appearance"
          >🎨</button>

          <transition name="drop-fade">
            <div v-if="showThemePicker" class="theme-popover" @click.stop>

              <!-- Dark / Light toggle -->
              <div class="tp-row">
                <span class="tp-label">Mode</span>
                <div class="tp-mode">
                  <button :class="['tp-mode-btn', !isLight ? 'tp-mode-on' : '']" @click="setLight(false)">🌙 Dark</button>
                  <button :class="['tp-mode-btn', isLight ? 'tp-mode-on' : '']" @click="setLight(true)">☀️ Light</button>
                </div>
              </div>

              <!-- Palette swatches -->
              <div class="tp-row">
                <span class="tp-label">Palette</span>
                <div class="tp-swatches">
                  <button
                    v-for="p in palettes"
                    :key="p.id"
                    class="tp-swatch"
                    :class="activePalette === p.id ? 'tp-swatch-on' : ''"
                    :style="`background:${p.bg}`"
                    :title="p.name"
                    @click="setPalette(p.id)"
                  >
                    <span class="tp-dot" :style="`background:${p.accent}`"></span>
                    <span class="tp-swatch-label">{{ p.name }}</span>
                  </button>
                </div>
              </div>

            </div>
          </transition>
        </div>

        <button
          class="nav-refresh-btn"
          :class="refreshingPrices ? 'nav-refresh-spin' : ''"
          @click="refreshPrices"
          :disabled="refreshingPrices"
          title="Refresh USDT equal on this page"
        >↻ USDT</button>

        <span v-if="currentUser" class="nav-user" :title="currentUser.email">
          👤 {{ currentUser.name || currentUser.email.split('@')[0] }}
        </span>
        <span class="nav-time">🕐 {{ now }}</span>
        <div class="ver-wrap" style="position:relative">
          <button
            class="nav-ver"
            :class="showChangelog ? 'nav-ver-on' : ''"
            @click.stop="showChangelog = !showChangelog"
            title="What's new"
          >v{{ version }}</button>

          <transition name="drop-fade">
            <div v-if="showChangelog" class="changelog-popover" @click.stop>
              <div class="cl-title">What's New</div>
              <div v-for="entry in changelog" :key="entry.version" class="cl-entry">
                <div class="cl-header">
                  <span class="cl-ver">v{{ entry.version }}</span>
                  <span class="cl-date">{{ entry.date }}</span>
                </div>
                <ul class="cl-list">
                  <li v-for="(item, i) in entry.changes" :key="i">{{ item }}</li>
                </ul>
              </div>
            </div>
          </transition>
        </div>
        <button v-if="isLoggedIn" class="nav-logout-btn" @click="logout" title="Logout">⏻</button>
      </div>
    </nav>

    <!-- Click-outside overlay -->
    <div v-if="openMenu || showThemePicker || showChangelog" class="nav-overlay" @click="openMenu = null; showThemePicker = false; showChangelog = false"></div>

    <!-- ── Body ── -->
    <div class="app-body">
      <Login v-if="!isLoggedIn" @login-success="onLogin" />
      <div v-else class="page-content">

        <!-- Chain banner shown on /tron, /solana, /ethereum etc. -->
        <div v-if="currentChain" class="chain-banner" :style="`--cc:${currentChain.color}`">
          <img :src="currentChain.logo" class="chain-banner-logo" :alt="currentChain.label" />
          <div class="chain-banner-info">
            <span class="chain-banner-name" :style="`color:${currentChain.color}`">{{ currentChain.label }}</span>
            <span class="chain-banner-sub">{{ routeIsTransactions ? 'Transactions' : 'Wallet' }}</span>
          </div>
          <div class="chain-banner-bar" :style="`background:${currentChain.color}`"></div>
        </div>

        <transition name="page-fade" mode="out-in">
          <router-view :key="$route.path" />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import 'bootstrap/dist/css/bootstrap.min.css';
import Login from './components/Login.vue';
import { VERSION, CHANGELOG } from './version';
import { pb, logoutUser, refreshAuth } from './services/pbService';
import eventBus from './services/eventBus';

import tronLogo    from './assets/tron-logo.svg';
import solanaLogo  from './assets/solana-logo.svg';
import ethLogo     from './assets/eth-logo.svg';
import bscLogo     from './assets/bsc-logo.svg';
import polygonLogo from './assets/polygon-logo.svg';
import bitcoinLogo from './assets/bitcoin-logo.svg';

export default {
  name: 'App',
  components: { Login },
  data() {
    return {
      isLoggedIn: pb.authStore.isValid,
      currentUser: pb.authStore.record,
      version: VERSION,
      now: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      openMenu: null,
      nowTimer: null,
      showThemePicker: false,
      showChangelog: false,
      refreshingPrices: false,
      changelog: CHANGELOG,
      activePalette: localStorage.getItem('hdwallet_palette') || 'cosmic',
      isLight: localStorage.getItem('hdwallet_light') === 'true',
      palettes: [
        { id: 'cosmic',   name: 'Cosmic',   bg: '#0b0d14', accent: '#6366f1' },
        { id: 'midnight', name: 'Midnight', bg: '#060608', accent: '#8b5cf6' },
        { id: 'ocean',    name: 'Ocean',    bg: '#060d14', accent: '#0ea5e9' },
        { id: 'forest',   name: 'Forest',   bg: '#060d08', accent: '#10b981' },
        { id: 'ember',    name: 'Ember',    bg: '#120a06', accent: '#f59e0b' },
        { id: 'aurora',   name: 'Aurora',   bg: '#0a060f', accent: '#c026d3' },
      ],
      chains: [
        { key: 'tron',     label: 'Tron',    color: '#FF0013', logo: tronLogo    },
        { key: 'solana',   label: 'Solana',  color: '#9945FF', logo: solanaLogo  },
        { key: 'ethereum', label: 'ETH',     color: '#627EEA', logo: ethLogo     },
        { key: 'bsc',      label: 'BSC',     color: '#F0B90B', logo: bscLogo     },
        { key: 'polygon',  label: 'Polygon', color: '#8247E5', logo: polygonLogo },
        { key: 'bitcoin',  label: 'BTC',     color: '#F7931A', logo: bitcoinLogo },
      ],
    };
  },
  computed: {
    currentChain() {
      const path = this.$route?.path;
      if (!path) return null;
      return this.chains.find(c => path.startsWith('/' + c.key)) || null;
    },
    routeIsTransactions() {
      return this.$route?.path?.includes('/transactions') || false;
    },
  },
  methods: {
    onLogin() {
      this.isLoggedIn = pb.authStore.isValid;
      this.currentUser = pb.authStore.record;
      this.$router.push('/dashboard');
    },
    async logout() {
      await logoutUser();
      this.isLoggedIn = false;
      this.currentUser = null;
      this.$router.push('/');
    },
    isChainActive(key) {
      return this.$route && this.$route.path.startsWith('/' + key);
    },
    applyTheme() {
      document.documentElement.setAttribute('data-theme', this.isLight ? 'light' : this.activePalette);
    },
    setPalette(id) {
      this.activePalette = id;
      localStorage.setItem('hdwallet_palette', id);
      this.applyTheme();
    },
    setLight(val) {
      this.isLight = val;
      localStorage.setItem('hdwallet_light', String(val));
      this.applyTheme();
    },
    // Tells whichever wallet page (or Dashboard) is currently mounted to
    // re-run its own price refresh right now, instead of waiting up to 60s
    // for its own auto-refresh timer.
    refreshPrices() {
      this.refreshingPrices = true;
      eventBus.$emit('refresh-prices');
      setTimeout(() => { this.refreshingPrices = false; }, 1000);
    },
  },
  mounted() {
    this.applyTheme();
    this.nowTimer = setInterval(() => {
      this.now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }, 30000);
    // Sync auth state on token changes (e.g. after refresh or logout in another tab)
    pb.authStore.onChange(() => {
      this.isLoggedIn = pb.authStore.isValid;
      this.currentUser = pb.authStore.record;
    });
    // Silently refresh token if already logged in
    if (pb.authStore.isValid) {
      refreshAuth().catch(() => {
        this.isLoggedIn = false;
        this.currentUser = null;
      });
    }
  },
  beforeDestroy() {
    clearInterval(this.nowTimer);
  },
};
</script>

<style>
/* ══════════════════════════════════════════════════════
   Design tokens
══════════════════════════════════════════════════════ */
:root {
  --bg:        #0b0d14;
  --surface:   #111520;
  --surface-2: #171b28;
  --surface-3: #1d2235;
  --border:    #242a3a;
  --border-2:  #2e3650;
  --text:      #c8cfe8;
  --text-2:    #8892b0;
  --text-muted:#52597a;
  --text-dim:  #363d57;
  --accent:    #6366f1;
  --accent-2:  #818cf8;
  --green:     #22c55e;
  --red:       #f43f5e;
  --yellow:    #f59e0b;
  --radius-lg: 14px;
  --radius:    10px;
  --radius-sm: 6px;
  --shadow:    0 4px 24px rgba(0,0,0,0.55);
  --shadow-sm: 0 2px 10px rgba(0,0,0,0.4);
  --ease:      all 0.18s ease;
}

/* ══════════════════════════════════════════════════════
   Base reset
══════════════════════════════════════════════════════ */
*, *::before, *::after { box-sizing: border-box; }
html { overflow-x: hidden; scroll-behavior: smooth; }
body {
  margin: 0;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', Roboto, Oxygen, Ubuntu, sans-serif;
  font-size: 14px;
  line-height: 1.55;
  min-height: 100vh;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}

/* Thin scrollbar */
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border-2); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: #3d4670; }

/* ══════════════════════════════════════════════════════
   Navbar
══════════════════════════════════════════════════════ */
.app-navbar {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 5px;
  background: var(--nb-bg, rgba(11,13,20,0.96));
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  padding: 8px 20px;
  border-bottom: 1px solid var(--border);
  box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 4px 20px rgba(0,0,0,0.4);
  flex-wrap: wrap;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.98rem;
  font-weight: 800;
  color: #e2e8f0 !important;
  text-decoration: none !important;
  margin-right: 12px;
  letter-spacing: -0.01em;
  white-space: nowrap;
}
.brand-icon {
  font-size: 1.3rem;
  background: linear-gradient(135deg, #6366f1, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.brand-text {
  background: linear-gradient(135deg, #e2e8f0, #94a3b8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 3px;
  flex: 1;
  flex-wrap: wrap;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-left: auto;
}
.nav-time {
  font-size: 0.72rem;
  color: var(--text-muted);
  background: var(--surface-2);
  padding: 3px 9px;
  border-radius: 20px;
  border: 1px solid var(--border);
  white-space: nowrap;
}
.nav-ver {
  font-size: 0.66rem;
  color: var(--text-dim);
  font-family: monospace;
  background: var(--surface);
  padding: 2px 7px;
  border-radius: 8px;
  border: 1px solid var(--border);
  cursor: pointer;
  transition: all 0.15s;
}
.nav-ver:hover,
.nav-ver-on {
  border-color: var(--accent) !important;
  color: var(--accent-2) !important;
  background: rgba(99,102,241,0.1) !important;
}

.changelog-popover {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 290px;
  background: var(--surface-2);
  border: 1px solid var(--border-2);
  border-radius: 14px;
  padding: 14px 16px;
  z-index: 1020;
  box-shadow: var(--shadow);
}

.cl-title {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 12px;
}

.cl-entry {
  margin-bottom: 14px;
}
.cl-entry:last-child { margin-bottom: 0; }

.cl-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.cl-ver {
  font-size: 0.78rem;
  font-weight: 800;
  font-family: monospace;
  color: var(--accent-2);
  background: rgba(99,102,241,0.12);
  border: 1px solid rgba(99,102,241,0.25);
  padding: 1px 7px;
  border-radius: 6px;
}

.cl-date {
  font-size: 0.68rem;
  color: var(--text-muted);
  font-family: monospace;
}

.cl-list {
  margin: 0;
  padding-left: 16px;
  list-style: disc;
}
.cl-list li {
  font-size: 0.76rem;
  color: var(--text-2);
  line-height: 1.6;
  padding: 0;
  margin: 0;
}

/* Pills */
.nav-lnk {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.77rem;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 20px;
  border: 1px solid var(--border);
  color: var(--text-muted);
  background: transparent;
  text-decoration: none !important;
  cursor: pointer;
  transition: var(--ease);
  white-space: nowrap;
}
.nav-lnk:hover {
  color: var(--text);
  border-color: var(--accent);
  background: rgba(99,102,241,0.08);
  text-decoration: none !important;
}
.nav-lnk-on {
  background: rgba(99,102,241,0.14) !important;
  color: var(--accent-2) !important;
  border-color: var(--accent) !important;
}

/* Chain pills */
.chain-pill { gap: 5px; }
.chain-pill-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  border-radius: 3px;
  flex-shrink: 0;
}
.drop-caret {
  font-size: 0.58rem;
  opacity: 0.7;
  margin-left: 1px;
  transition: transform 0.15s;
}
.caret-up { transform: rotate(180deg); }

/* Dropdown */
.nav-drop-wrap { position: relative; }
.nav-drop-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 162px;
  background: var(--surface-2);
  border: 1px solid var(--border-2);
  border-radius: var(--radius);
  padding: 5px 0;
  z-index: 1010;
  box-shadow: var(--shadow);
}
.drop-fade-enter-active,
.drop-fade-leave-active { transition: opacity 0.12s, transform 0.12s; }
.drop-fade-enter,
.drop-fade-leave-to { opacity: 0; transform: translateY(-6px); }

.nav-drop-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text);
  text-decoration: none !important;
  transition: background 0.1s;
}
.nav-drop-item:hover { background: var(--surface-3); color: #fff !important; }
.nav-drop-item.router-link-active { color: var(--accent-2) !important; font-weight: 700; }
.drop-icon { font-size: 0.78em; opacity: 0.75; }

/* Overlay */
.nav-overlay { position: fixed; inset: 0; z-index: 999; }

/* ══════════════════════════════════════════════════════
   Body / page layout
══════════════════════════════════════════════════════ */
.app-body { min-height: calc(100vh - 54px); }

.page-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 22px 24px 48px;
}

/* Page transition */
.page-fade-enter-active,
.page-fade-leave-active { transition: opacity 0.15s ease; }
.page-fade-enter,
.page-fade-leave-to { opacity: 0; }

/* ══════════════════════════════════════════════════════
   Total USDT value banner (wallet balance tables)
══════════════════════════════════════════════════════ */
.total-usdt-banner {
  display: flex;
  align-items: baseline;
  gap: 8px;
  background: linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.06));
  border: 1px solid rgba(99,102,241,0.25);
  border-radius: var(--radius);
  padding: 10px 16px;
  margin-bottom: 12px;
}
.total-usdt-banner .tub-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--text-2);
}
.total-usdt-banner .tub-value {
  font-size: 1.25rem;
  font-weight: 800;
  background: linear-gradient(135deg, #818cf8, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.01em;
}
.total-usdt-banner .tub-fast,
.total-usdt-banner .tub-refresh {
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text-muted);
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1;
}
.total-usdt-banner .tub-fast {
  margin-left: auto;
  padding: 5px 12px;
  font-size: 0.7rem;
  font-weight: 700;
}
.total-usdt-banner .tub-refresh {
  width: 26px;
  height: 26px;
  padding: 0;
}
.total-usdt-banner .tub-fast .spinner-border,
.total-usdt-banner .tub-refresh .spinner-border {
  width: 0.8rem !important;
  height: 0.8rem !important;
  border-width: 2px !important;
}
.total-usdt-banner .tub-fast:hover:not(:disabled),
.total-usdt-banner .tub-refresh:hover:not(:disabled) {
  border-color: var(--accent);
  background: rgba(99,102,241,0.12);
  color: var(--text);
}
.total-usdt-banner .tub-fast:disabled,
.total-usdt-banner .tub-refresh:disabled { cursor: default; opacity: 0.7; }

/* ══════════════════════════════════════════════════════
   Chain banner
══════════════════════════════════════════════════════ */
.chain-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0 12px;
  margin-bottom: 20px;
  position: relative;
}
.chain-banner-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  border-radius: 1px;
  opacity: 0.6;
}
.chain-banner-logo {
  width: 38px;
  height: 38px;
  object-fit: contain;
  border-radius: 10px;
  padding: 5px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  flex-shrink: 0;
}
.chain-banner-info {
  display: flex;
  flex-direction: column;
  line-height: 1;
}
.chain-banner-name {
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.025em;
}
.chain-banner-sub {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 4px;
}

/* ══════════════════════════════════════════════════════
   Bootstrap dark overrides
══════════════════════════════════════════════════════ */

/* Tables */
.table { color: var(--text) !important; background: transparent; margin-bottom: 0; }
.table td, .table th {
  border-color: var(--border) !important;
  vertical-align: middle !important;
  padding: 10px 14px !important;
  font-size: 0.83rem !important;
}
.table thead th {
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid var(--border) !important;
  white-space: nowrap;
}
.table-striped tbody tr:nth-of-type(odd) { background: rgba(255,255,255,0.016) !important; }
.table-striped tbody tr:hover,
.table-hover  tbody tr:hover { background: rgba(255,255,255,0.04) !important; }
.table-sm td, .table-sm th { padding: 7px 12px !important; }

/* Borders */
.table-bordered { border: 1px solid var(--border) !important; }
.table-bordered td, .table-bordered th { border: 1px solid var(--border) !important; }

/* Cards */
.card {
  background: var(--surface) !important;
  border: 1px solid var(--border) !important;
  border-radius: var(--radius) !important;
  box-shadow: var(--shadow-sm) !important;
  color: var(--text) !important;
  transition: box-shadow 0.2s, transform 0.2s;
}
.card:hover { box-shadow: var(--shadow) !important; }
.card-header {
  background: var(--surface-2) !important;
  border-bottom: 1px solid var(--border) !important;
  color: var(--text) !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  text-transform: uppercase !important;
  padding: 12px 18px !important;
  border-radius: var(--radius) var(--radius) 0 0 !important;
}
.card-body { padding: 18px !important; }
.card-title { color: var(--text) !important; font-weight: 700 !important; font-size: 0.9rem !important; }

/* Form controls */
.form-control {
  background: var(--surface-2) !important;
  border: 1px solid var(--border-2) !important;
  color: var(--text) !important;
  border-radius: var(--radius-sm) !important;
  font-size: 0.84rem !important;
  transition: var(--ease) !important;
  height: auto !important;
}
.form-control:focus {
  background: var(--surface-3) !important;
  border-color: var(--accent) !important;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.18) !important;
  color: var(--text) !important;
  outline: none !important;
}
.form-control::placeholder { color: var(--text-muted) !important; opacity: 1; }
.form-control option { background: var(--surface-2); color: var(--text); }
select.form-control { appearance: auto; }

/* Input groups */
.input-group-text {
  background: var(--surface-3) !important;
  border: 1px solid var(--border-2) !important;
  color: var(--text-muted) !important;
  font-size: 0.78rem !important;
}

/* Labels */
label {
  color: var(--text-2);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-bottom: 5px;
}

/* Buttons */
.btn {
  border-radius: var(--radius-sm) !important;
  font-weight: 600 !important;
  font-size: 0.81rem !important;
  padding: 6px 14px !important;
  transition: var(--ease) !important;
  letter-spacing: 0.02em !important;
}
.btn:active { transform: scale(0.96) !important; }

.btn-primary {
  background: var(--accent) !important;
  border-color: var(--accent) !important;
  color: #fff !important;
}
.btn-primary:hover {
  background: #4f51cc !important;
  border-color: #4f51cc !important;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.25) !important;
  color: #fff !important;
}

.btn-secondary {
  background: var(--surface-2) !important;
  border-color: var(--border-2) !important;
  color: var(--text-2) !important;
}
.btn-secondary:hover {
  background: var(--surface-3) !important;
  border-color: var(--border-2) !important;
  color: var(--text) !important;
}

.btn-outline-secondary {
  background: transparent !important;
  border-color: var(--border-2) !important;
  color: var(--text-muted) !important;
}
.btn-outline-secondary:hover {
  background: var(--surface-2) !important;
  color: var(--text) !important;
  border-color: var(--border-2) !important;
}

.btn-danger {
  background: rgba(244,63,94,0.15) !important;
  border-color: rgba(244,63,94,0.4) !important;
  color: #f87171 !important;
}
.btn-danger:hover {
  background: rgba(244,63,94,0.3) !important;
  color: #fff !important;
}

.btn-info {
  background: rgba(99,153,251,0.15) !important;
  border-color: rgba(99,153,251,0.4) !important;
  color: #93c5fd !important;
}
.btn-info:hover {
  background: rgba(99,153,251,0.3) !important;
  color: #fff !important;
}

.btn-warning {
  background: rgba(245,158,11,0.15) !important;
  border-color: rgba(245,158,11,0.4) !important;
  color: #fcd34d !important;
}
.btn-warning:hover {
  background: rgba(245,158,11,0.3) !important;
  color: #fff !important;
}

.btn-success {
  background: rgba(34,197,94,0.15) !important;
  border-color: rgba(34,197,94,0.4) !important;
  color: #86efac !important;
}
.btn-success:hover {
  background: rgba(34,197,94,0.3) !important;
  color: #fff !important;
}

.btn-outline-primary {
  background: transparent !important;
  border-color: var(--accent) !important;
  color: var(--accent-2) !important;
}
.btn-outline-primary:hover {
  background: rgba(99,102,241,0.12) !important;
  color: #fff !important;
}

/* Badges */
.badge {
  font-size: 0.68rem !important;
  font-weight: 700 !important;
  padding: 3px 8px !important;
  border-radius: 8px !important;
  letter-spacing: 0.05em !important;
}
.badge-success   { background: rgba(34,197,94,0.18) !important;  color: #86efac !important; }
.badge-primary   { background: rgba(99,102,241,0.18) !important; color: #a5b4fc !important; }
.badge-secondary { background: var(--surface-3) !important;      color: var(--text-muted) !important; }
.badge-danger    { background: rgba(244,63,94,0.18) !important;  color: #fca5a5 !important; }
.badge-warning   { background: rgba(245,158,11,0.18) !important; color: #fcd34d !important; }

/* Alerts */
.alert {
  border-radius: var(--radius) !important;
  border: 1px solid var(--border-2) !important;
  background: var(--surface) !important;
  color: var(--text) !important;
  padding: 14px 18px !important;
}

/* Modals (vue-modal / vm--modal) */
.v--modal-box, .vm--modal {
  background: var(--surface) !important;
  border: 1px solid var(--border-2) !important;
  border-radius: var(--radius-lg) !important;
  box-shadow: var(--shadow) !important;
  color: var(--text) !important;
}
.vm--overlay { background: rgba(0,0,0,0.7) !important; }
.modal-content {
  background: var(--surface) !important;
  border: 1px solid var(--border-2) !important;
  border-radius: var(--radius) !important;
  color: var(--text) !important;
}
.modal-header {
  border-bottom: 1px solid var(--border) !important;
  padding: 14px 18px !important;
}
.modal-footer { border-top: 1px solid var(--border) !important; }
.modal-title { color: var(--text) !important; font-weight: 700 !important; }

/* Text utilities */
.text-muted   { color: var(--text-muted) !important; }
.text-success { color: var(--green) !important; }
.text-danger  { color: var(--red) !important; }
.text-break   { word-break: break-all !important; }

/* Spinner */
.spinner-border { border-width: 2px !important; width: 2rem !important; height: 2rem !important; }

/* Misc */
.bold { font-weight: 700; }

/* ══════════════════════════════════════════════════════
   Wallet page layout helpers
══════════════════════════════════════════════════════ */
.page-content .mt-5 { margin-top: 8px !important; }
.page-content .container-fluid { padding-left: 0 !important; padding-right: 0 !important; }

/* ══════════════════════════════════════════════════════
   Dark palette themes
══════════════════════════════════════════════════════ */
:root,
[data-theme="cosmic"] {
  --bg: #0b0d14; --surface: #111520; --surface-2: #171b28; --surface-3: #1d2235;
  --border: #242a3a; --border-2: #2e3650;
  --text: #c8cfe8; --text-2: #8892b0; --text-muted: #52597a; --text-dim: #363d57;
  --accent: #6366f1; --accent-2: #818cf8;
  --shadow: 0 4px 24px rgba(0,0,0,0.55); --shadow-sm: 0 2px 10px rgba(0,0,0,0.4);
  --nb-bg: rgba(11,13,20,0.96);
}
[data-theme="midnight"] {
  --bg: #060608; --surface: #0e0e12; --surface-2: #141418; --surface-3: #1a1a20;
  --border: #222228; --border-2: #2c2c36;
  --text: #d4d0e8; --text-2: #8880a8; --text-muted: #504868; --text-dim: #303048;
  --accent: #8b5cf6; --accent-2: #a78bfa;
  --shadow: 0 4px 24px rgba(0,0,0,0.7); --shadow-sm: 0 2px 10px rgba(0,0,0,0.55);
  --nb-bg: rgba(6,6,8,0.97);
}
[data-theme="ocean"] {
  --bg: #060d14; --surface: #0c1520; --surface-2: #111d2b; --surface-3: #162436;
  --border: #1e2f40; --border-2: #274050;
  --text: #b8d8e8; --text-2: #6888a0; --text-muted: #405060; --text-dim: #284058;
  --accent: #0ea5e9; --accent-2: #38bdf8;
  --shadow: 0 4px 24px rgba(0,0,0,0.6); --shadow-sm: 0 2px 10px rgba(0,0,0,0.45);
  --nb-bg: rgba(6,13,20,0.96);
}
[data-theme="forest"] {
  --bg: #060d08; --surface: #0c150e; --surface-2: #111d13; --surface-3: #162518;
  --border: #1e3020; --border-2: #27402a;
  --text: #b8d8bc; --text-2: #6899a0; --text-muted: #405848; --text-dim: #284030;
  --accent: #10b981; --accent-2: #34d399;
  --shadow: 0 4px 24px rgba(0,0,0,0.6); --shadow-sm: 0 2px 10px rgba(0,0,0,0.45);
  --nb-bg: rgba(6,13,8,0.96);
}
[data-theme="ember"] {
  --bg: #120a06; --surface: #1c1008; --surface-2: #261810; --surface-3: #301f12;
  --border: #3d2814; --border-2: #4e3318;
  --text: #e8d0b8; --text-2: #a07868; --text-muted: #685040; --text-dim: #483020;
  --accent: #f59e0b; --accent-2: #fbbf24;
  --shadow: 0 4px 24px rgba(0,0,0,0.65); --shadow-sm: 0 2px 10px rgba(0,0,0,0.5);
  --nb-bg: rgba(18,10,6,0.97);
}
[data-theme="aurora"] {
  --bg: #0a060f; --surface: #130c1a; --surface-2: #1a1024; --surface-3: #22152e;
  --border: #2e1e42; --border-2: #3d2858;
  --text: #d8c8e8; --text-2: #9080b0; --text-muted: #584870; --text-dim: #382850;
  --accent: #c026d3; --accent-2: #d946ef;
  --shadow: 0 4px 24px rgba(0,0,0,0.65); --shadow-sm: 0 2px 10px rgba(0,0,0,0.5);
  --nb-bg: rgba(10,6,15,0.97);
}

/* ══════════════════════════════════════════════════════
   Light theme
══════════════════════════════════════════════════════ */
[data-theme="light"] {
  --bg: #f4f6fb; --surface: #ffffff; --surface-2: #edf0f7; --surface-3: #e4e8f2;
  --border: #d4d8e8; --border-2: #c0c6d8;
  --text: #1a1f38; --text-2: #424870; --text-muted: #8088a8; --text-dim: #b0b8cc;
  --accent: #6366f1; --accent-2: #4f52cc;
  --green: #16a34a; --red: #dc2626; --yellow: #d97706;
  --shadow: 0 4px 24px rgba(0,0,0,0.1); --shadow-sm: 0 2px 10px rgba(0,0,0,0.07);
  --nb-bg: rgba(244,246,251,0.97);
}
/* Light-specific navbar */
[data-theme="light"] .app-navbar {
  background: var(--nb-bg);
  border-bottom-color: var(--border);
  box-shadow: 0 1px 0 rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.06);
}
[data-theme="light"] .nav-brand { color: #1a1f38 !important; }
[data-theme="light"] .brand-text {
  background: linear-gradient(135deg, #1a1f38, #424870);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
[data-theme="light"] .nav-lnk { color: var(--text-muted); border-color: var(--border); }
[data-theme="light"] .nav-lnk:hover { background: rgba(99,102,241,0.07); color: var(--text); border-color: var(--accent); }
[data-theme="light"] .nav-lnk-on { background: rgba(99,102,241,0.12) !important; color: #4f52cc !important; }
/* Light-mode table tweaks */
[data-theme="light"] .table { color: var(--text) !important; }
[data-theme="light"] .table-striped tbody tr:nth-of-type(odd) { background: rgba(0,0,0,0.018) !important; }
[data-theme="light"] .table-striped tbody tr:hover,
[data-theme="light"] .table-hover  tbody tr:hover { background: rgba(99,102,241,0.05) !important; }
/* Scrollbar for light */
[data-theme="light"] ::-webkit-scrollbar-thumb { background: var(--border-2); }
[data-theme="light"] ::-webkit-scrollbar-thumb:hover { background: #a0a8c0; }

/* ══════════════════════════════════════════════════════
   Theme picker button + popover
══════════════════════════════════════════════════════ */
.nav-theme-btn {
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text-muted);
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1;
}
.nav-theme-btn:hover,
.nav-theme-btn-on {
  border-color: var(--accent);
  background: rgba(99,102,241,0.1);
  color: var(--text);
}

.theme-popover {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 260px;
  background: var(--surface-2);
  border: 1px solid var(--border-2);
  border-radius: 14px;
  padding: 14px;
  z-index: 1020;
  box-shadow: var(--shadow);
}

.tp-row {
  margin-bottom: 12px;
}
.tp-row:last-child { margin-bottom: 0; }

.tp-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
}

/* Mode toggle (Dark / Light) */
.tp-mode {
  display: flex;
  gap: 6px;
}
.tp-mode-btn {
  flex: 1;
  background: var(--surface-3);
  border: 1px solid var(--border-2);
  color: var(--text-muted);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
}
.tp-mode-btn:hover { color: var(--text); border-color: var(--accent); }
.tp-mode-on {
  background: rgba(99,102,241,0.16) !important;
  border-color: var(--accent) !important;
  color: var(--accent-2) !important;
}

/* Palette swatches */
.tp-swatches {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.tp-swatch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 8px 6px 7px;
  border-radius: 10px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.14s;
}
.tp-swatch:hover { border-color: rgba(255,255,255,0.2); transform: translateY(-1px); }
.tp-swatch-on { border-color: rgba(255,255,255,0.5) !important; box-shadow: 0 0 0 2px rgba(255,255,255,0.15); }

.tp-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: block;
  box-shadow: 0 2px 6px rgba(0,0,0,0.4);
}
.tp-swatch-label {
  font-size: 0.62rem;
  font-weight: 600;
  color: rgba(255,255,255,0.65);
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.tp-swatch-on .tp-swatch-label { color: rgba(255,255,255,0.95); }

.nav-user {
  font-size: 0.72rem;
  color: var(--text-2);
  background: var(--surface-2);
  padding: 3px 9px;
  border-radius: 20px;
  border: 1px solid var(--border);
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-refresh-btn {
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text-muted);
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.nav-refresh-btn:hover:not(:disabled) {
  border-color: var(--accent);
  background: rgba(99,102,241,0.1);
  color: var(--text);
}
.nav-refresh-btn:disabled { cursor: default; opacity: 0.8; }
.nav-refresh-spin { color: var(--accent-2) !important; border-color: var(--accent) !important; }

.nav-logout-btn {
  background: transparent;
  border: 1px solid rgba(244,63,94,0.35);
  color: rgba(244,63,94,0.7);
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1;
}
.nav-logout-btn:hover {
  border-color: rgba(244,63,94,0.8);
  color: #f87171;
  background: rgba(244,63,94,0.1);
}
</style>
